// src/components/Dashboard.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, setDoc, collection, addDoc, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import './Dashboard.css';

const Dashboard = () => {
  const [error, setError] = useState('');
  const [subscription, setSubscription] = useState('free');
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usageOverview, setUsageOverview] = useState(null);
  const [selectedKeyUsage, setSelectedKeyUsage] = useState(null);
  const [keysLimit, setKeysLimit] = useState({
    limit: 2,
    remaining: 2,
    resetTime: null
  });
  
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const db = getFirestore();

  // Auto-refresh usage data every 30 seconds
  useEffect(() => {
    if (!currentUser || apiKeys.length === 0) return;

    const interval = setInterval(() => {
      fetchUsageOverview();
      if (selectedKeyUsage) {
        fetchKeyUsage(selectedKeyUsage.api_key_id);
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [currentUser, apiKeys.length, selectedKeyUsage]);

  // Fetch user data and API keys on component mount
  useEffect(() => {
    async function fetchUserData() {
      if (currentUser) {
        try {
          setLoading(true);
          
          // Get user profile including subscription
          const userDoc = await getDoc(doc(db, "users", currentUser.uid));
          
          if (userDoc.exists()) {
            setSubscription(userDoc.data().subscription || 'free');
          } else {
            await setDoc(doc(db, "users", currentUser.uid), {
              email: currentUser.email,
              subscription: 'free',
              createdAt: new Date()
            });
          }
          
          // Get user's API keys
          const keysQuery = query(
            collection(db, "api_keys"), 
            where("userId", "==", currentUser.uid)
          );
          
          const keysSnapshot = await getDocs(keysQuery);
          const keys = [];
          
          keysSnapshot.forEach(doc => {
            keys.push({
              id: doc.id,
              ...doc.data()
            });
          });
          
          setApiKeys(keys);
          
          // Get usage overview
          await fetchUsageOverview();
          
          // Calculate remaining keys for free tier
          const currentSubscription = userDoc.exists() ? userDoc.data().subscription || 'free' : 'free';
          
          if (currentSubscription === 'free') {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            const keysCreatedToday = keys.filter(key => {
              const keyDate = key.createdAt instanceof Date 
                ? key.createdAt 
                : key.createdAt.toDate ? key.createdAt.toDate() : new Date(key.createdAt);
              return keyDate >= today;
            });
            
            setKeysLimit({
              limit: 2,
              remaining: Math.max(0, 2 - keysCreatedToday.length),
              resetTime: new Date(today.getTime() + 24 * 60 * 60 * 1000)
            });
          } else {
            setKeysLimit({
              limit: null,
              remaining: null, 
              resetTime: null
            });
          }
        } catch (err) {
          console.error("Error fetching user data:", err);
          setError("Failed to load user data");
        } finally {
          setLoading(false);
        }
      }
    }
    
    fetchUserData();
  }, [currentUser, db]);

  // Fetch usage overview
  const fetchUsageOverview = async () => {
    try {
      const response = await fetch('/api/user/usage-overview', {
        headers: {
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        }
      });
      
      if (response.ok) {
        const overview = await response.json();
        setUsageOverview(overview);
      } else {
        // Use mock data if endpoint isn't available
        setUsageOverview({
          total_keys: apiKeys.length,
          active_keys: apiKeys.length,
          total_requests_today: 25,
          total_requests_month: 750,
          keys_near_limit: 0
        });
      }
    } catch (err) {
      console.error("Error fetching usage overview:", err);
      // Use mock data on error
      setUsageOverview({
        total_keys: apiKeys.length,
        active_keys: apiKeys.length,
        total_requests_today: 15,
        total_requests_month: 450,
        keys_near_limit: 0
      });
    }
  };

  // Fetch detailed usage for a specific API key
  const fetchKeyUsage = async (apiKey) => {
    try {
      const response = await fetch('/api/usage-stats', {
        headers: {
          'X-API-Key': apiKey
        }
      });
      
      if (response.ok) {
        const usage = await response.json();
        setSelectedKeyUsage(usage);
      }
    } catch (err) {
      console.error("Error fetching key usage:", err);
    }
  };

  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    
    try {
      if (timestamp && typeof timestamp.toDate === 'function') {
        return timestamp.toDate().toLocaleDateString();
      }
      
      if (typeof timestamp === 'number') {
        return new Date(timestamp * 1000).toLocaleDateString();
      }
      
      if (timestamp instanceof Date) {
        return timestamp.toLocaleDateString();
      }
      
      if (typeof timestamp === 'string') {
        return new Date(timestamp).toLocaleDateString();
      }
      
      if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleDateString();
      }
      
      return 'Invalid date';
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date error';
    }
  };

  // Usage alert level helper
  const getUsageAlertLevel = (usage, limit) => {
    const percentage = (usage / limit) * 100;
    if (percentage >= 90) return 'critical';
    if (percentage >= 80) return 'warning';
    return 'normal';
  };

  // Generate API key function
  const generateApiKey = async () => {
    try {
      if (subscription === 'free' && keysLimit.remaining <= 0) {
        setError("Free tier users can only create 2 API keys per day. Please upgrade your plan for unlimited API keys.");
        return;
      }
      
      setError('');
      
      const timestamp = Math.floor(Date.now() / 1000).toString();
      const secret = "change_this_secret_key";
      const message = `${currentUser.uid}:${timestamp}:${subscription}`;
      
      const encoder = new TextEncoder();
      const key = await crypto.subtle.importKey(
        'raw',
        encoder.encode(secret),
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
      );
      
      const signature = await crypto.subtle.sign(
        'HMAC',
        key,
        encoder.encode(message)
      );
      
      const signatureArray = Array.from(new Uint8Array(signature));
      const signatureHex = signatureArray.map(b => b.toString(16).padStart(2, '0')).join('');
      
      const newApiKey = `cinder_${timestamp}_${signatureHex.substring(0, 32)}`;
      
      const apiKeysRef = collection(db, "api_keys");
      
      const newKey = {
        key: newApiKey,
        userId: currentUser.uid,
        tier: subscription,
        createdAt: new Date(),
        expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        lastUsed: null,
        usageCount: 0,
        active: true
      };
      
      const docRef = await addDoc(apiKeysRef, newKey);
      
      setApiKeys([...apiKeys, { id: docRef.id, ...newKey }]);
      
      if (subscription === 'free') {
        setKeysLimit(prev => ({
          ...prev,
          remaining: Math.max(0, prev.remaining - 1)
        }));
      }
      
      await fetchUsageOverview();
      
    } catch (err) {
      console.error("Error generating API key:", err);
      setError("Failed to generate API key");
    }
  };

  // Revoke API key function
  const revokeApiKey = async (keyId) => {
    try {
      const keyRef = doc(db, "api_keys", keyId);
      const keyDoc = await getDoc(keyRef);
      
      if (!keyDoc.exists()) {
        setError("API key not found");
        return;
      }
      
      if (keyDoc.data().userId !== currentUser.uid) {
        setError("You don't have permission to revoke this key");
        return;
      }
      
      await deleteDoc(keyRef);
      setApiKeys(apiKeys.filter(key => key.id !== keyId));
      
      if (selectedKeyUsage && selectedKeyUsage.api_key_id === keyId) {
        setSelectedKeyUsage(null);
      }
      
      await fetchUsageOverview();
      
    } catch (err) {
      console.error("Error revoking API key:", err);
      setError(`Failed to revoke API key: ${err.message}`);
    }
  };

  // Update subscription function
  const updateSubscription = async (newTier) => {
    try {
      await setDoc(doc(db, "users", currentUser.uid), {
        subscription: newTier
      }, { merge: true });
      
      setSubscription(newTier);
      
    } catch (err) {
      console.error("Error updating subscription:", err);
      setError("Failed to update subscription");
    }
  };

  async function handleLogout() {
    setError('');
    try {
      await logout();
      navigate('/login');
    } catch {
      setError('Failed to log out');
    }
  }

  if (loading) {
    return <div className="dashboard-loading">Loading...</div>;
  }

  return (
    <section className="dashboard">
      <div className="dashboard-container">
        <h2>Your Cinder Dashboard</h2>
        {error && <div className="dashboard-error">{error}</div>}
        
        {/* Usage Overview Card */}
        {usageOverview && (
          <div className="dashboard-card">
            <h3>Usage Overview</h3>
            <div className="usage-overview-grid">
              <div className="usage-stat">
                <div className="usage-stat-value">{usageOverview.total_requests_today}</div>
                <div className="usage-stat-label">Requests Today</div>
              </div>
              <div className="usage-stat">
                <div className="usage-stat-value">{usageOverview.total_requests_month}</div>
                <div className="usage-stat-label">Requests This Month</div>
              </div>
              <div className="usage-stat">
                <div className="usage-stat-value">{usageOverview.active_keys}</div>
                <div className="usage-stat-label">Active API Keys</div>
              </div>
              <div className="usage-stat">
                <div className="usage-stat-value">{usageOverview.keys_near_limit}</div>
                <div className="usage-stat-label">Keys Near Limit</div>
              </div>
            </div>
          </div>
        )}
        
        {/* Profile Card */}
        <div className="dashboard-card">
          <h3>Profile</h3>
          <div className="profile-info">
            <div className="profile-row">
              <span className="profile-label">Email:</span>
              <span className="profile-value">{currentUser.email}</span>
            </div>
            <div className="profile-row">
              <span className="profile-label">Current Plan:</span>
              <span className="profile-value subscription-badge">
                {subscription.charAt(0).toUpperCase() + subscription.slice(1)}
              </span>
            </div>
          </div>
        </div>
        
        {/* Subscription Tiers */}
        <div className="dashboard-card">
          <h3>Subscription Tiers</h3>
          <div className="subscription-tiers">
            <div className={`tier-card ${subscription === 'free' ? 'active' : ''}`}>
              <h4>Free</h4>
              <div className="tier-price">$0/month</div>
              <ul className="tier-features">
                <li>Basic model analysis</li>
                <li>1 model at a time</li>
                <li>100 API requests/day</li>
                <li>2 API keys/day</li>
                <li>Community support</li>
              </ul>
              <button 
                className="tier-button"
                onClick={() => updateSubscription('free')}
                disabled={subscription === 'free'}
              >
                {subscription === 'free' ? 'Current Plan' : 'Downgrade'}
              </button>
            </div>
            
            <div className={`tier-card ${subscription === 'basic' ? 'active' : ''}`}>
              <h4>Basic</h4>
              <div className="tier-price">$9.99/month</div>
              <ul className="tier-features">
                <li>Advanced model analysis</li>
                <li>Up to 5 models</li>
                <li>1,000 API requests/day</li>
                <li>Unlimited API keys</li>
                <li>Error analysis</li>
                <li>Email support</li>
              </ul>
              <button 
                className="tier-button"
                onClick={() => updateSubscription('basic')}
                disabled={subscription === 'basic'}
              >
                {subscription === 'basic' ? 'Current Plan' : subscription === 'pro' ? 'Downgrade' : 'Upgrade'}
              </button>
            </div>
            
            <div className={`tier-card ${subscription === 'pro' ? 'active' : ''}`}>
              <h4>Professional</h4>
              <div className="tier-price">$29.99/month</div>
              <ul className="tier-features">
                <li>Everything in Basic</li>
                <li>Unlimited models</li>
                <li>10,000 API requests/day</li>
                <li>Unlimited API keys</li>
                <li>Bit Assistant</li>
                <li>Priority support</li>
              </ul>
              <button 
                className="tier-button"
                onClick={() => updateSubscription('pro')}
                disabled={subscription === 'pro'}
              >
                {subscription === 'pro' ? 'Current Plan' : 'Upgrade'}
              </button>
            </div>
          </div>
          <div className="subscription-note">
            <p>* This is a demo. No actual payment is processed.</p>
          </div>
        </div>
        
        {/* API Key Management with Usage Details */}
        <div className="dashboard-card">
          <h3>API Key Management</h3>
          <div className="api-keys-container">
            {apiKeys.length === 0 ? (
              <div className="no-keys">
                <p>You don't have any API keys yet. Generate one to get started.</p>
              </div>
            ) : (
              <div className="api-keys-list">
                {apiKeys.map(key => (
                  <ApiKeyItem 
                    key={key.id}
                    keyData={key}
                    onViewUsage={fetchKeyUsage}
                    onRevoke={revokeApiKey}
                    formatDate={formatDate}
                    getUsageAlertLevel={getUsageAlertLevel}
                  />
                ))}
              </div>
            )}
            
            {/* Key Usage Details Modal/Panel */}
            {selectedKeyUsage && (
              <div className="usage-details-panel">
                <div className="usage-details-header">
                  <h4>API Key Usage Details</h4>
                  <button 
                    className="close-usage-details"
                    onClick={() => setSelectedKeyUsage(null)}
                  >
                    ×
                  </button>
                </div>
                
                <div className="usage-details-content">
                  <div className="usage-metrics-grid">
                    <div className="usage-metric">
                      <div className="usage-metric-label">Daily Usage</div>
                      <div className="usage-metric-value">
                        {selectedKeyUsage.daily_usage} / {selectedKeyUsage.daily_limit}
                      </div>
                      <div className="usage-progress-bar">
                        <div 
                          className="usage-progress-fill"
                          style={{ 
                            width: `${Math.min(100, (selectedKeyUsage.daily_usage / selectedKeyUsage.daily_limit) * 100)}%`,
                            backgroundColor: getUsageAlertLevel(selectedKeyUsage.daily_usage, selectedKeyUsage.daily_limit) === 'critical' ? '#ff4444' : 
                                           getUsageAlertLevel(selectedKeyUsage.daily_usage, selectedKeyUsage.daily_limit) === 'warning' ? '#FF9800' : '#4CAF50'
                          }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="usage-metric">
                      <div className="usage-metric-label">Monthly Usage</div>
                      <div className="usage-metric-value">
                        {selectedKeyUsage.monthly_usage} / {selectedKeyUsage.monthly_limit}
                      </div>
                      <div className="usage-progress-bar">
                        <div 
                          className="usage-progress-fill"
                          style={{ 
                            width: `${Math.min(100, (selectedKeyUsage.monthly_usage / selectedKeyUsage.monthly_limit) * 100)}%`,
                            backgroundColor: getUsageAlertLevel(selectedKeyUsage.monthly_usage, selectedKeyUsage.monthly_limit) === 'critical' ? '#ff4444' : 
                                           getUsageAlertLevel(selectedKeyUsage.monthly_usage, selectedKeyUsage.monthly_limit) === 'warning' ? '#FF9800' : '#4CAF50'
                          }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="usage-metric">
                      <div className="usage-metric-label">Total Requests</div>
                      <div className="usage-metric-value">{selectedKeyUsage.total_requests}</div>
                    </div>
                    
                    <div className="usage-metric">
                      <div className="usage-metric-label">Last Used</div>
                      <div className="usage-metric-value">
                        {selectedKeyUsage.last_used 
                          ? new Date(selectedKeyUsage.last_used).toLocaleDateString() 
                          : 'Never'
                        }
                      </div>
                    </div>
                  </div>
                  
                  {selectedKeyUsage.reset_times && (
                    <div className="usage-reset-info">
                      <p><strong>Reset Times:</strong></p>
                      <p>Daily limit resets: {new Date(selectedKeyUsage.reset_times.daily_reset).toLocaleString()}</p>
                      <p>Monthly limit resets: {new Date(selectedKeyUsage.reset_times.monthly_reset).toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Key Limit Information */}
            {subscription === 'free' && (
              <div className="api-key-limits">
                <p>
                  Free tier users can create {keysLimit.limit} API keys per day. 
                  You have {keysLimit.remaining} remaining today.
                  {keysLimit.remaining === 0 && (
                    <span> Limit resets at midnight.</span>
                  )}
                </p>
              </div>
            )}
            
            <div className="api-key-generate">
              <button 
                className="generate-button"
                onClick={generateApiKey}
                disabled={subscription === 'free' && keysLimit.remaining <= 0}
              >
                Generate New API Key
              </button>
            </div>
          </div>
        </div>
        
        <div className="dashboard-actions">
          <button onClick={handleLogout} className="logout-button">
            Log Out
          </button>
        </div>
      </div>
    </section>
  );
};

// Enhanced API Key Item Component
const ApiKeyItem = ({ keyData, onViewUsage, onRevoke, formatDate, getUsageAlertLevel }) => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchUsage = async () => {
    if (!keyData.key) return;
    
    setLoading(true);
    try {
      const response = await fetch('/api/usage-stats', {
        headers: {
          'X-API-Key': keyData.key
        }
      });
      
      if (response.ok) {
        const usageData = await response.json();
        setUsage(usageData);
      }
    } catch (err) {
      console.error("Error fetching usage:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsage();
  }, [keyData.key]);

  const dailyAlertLevel = usage ? getUsageAlertLevel(usage.daily_usage, usage.daily_limit) : 'normal';
  const monthlyAlertLevel = usage ? getUsageAlertLevel(usage.monthly_usage, usage.monthly_limit) : 'normal';

  return (
    <div className="api-key-item">
      <div className="api-key-info">
        <div className="api-key-value">{keyData.key}</div>
        <div className="api-key-meta">
          <span className="api-key-tier">{keyData.tier} tier</span>
          <span className="api-key-created">
            Created: {formatDate(keyData.createdAt)}
          </span>
          <span className="api-key-usage">
            Total uses: {keyData.usageCount || 0}
          </span>
        </div>
        
        {/* Quick usage preview */}
        {usage && (
          <div className="api-key-usage-preview">
            <div className="usage-preview-item">
              <span className="usage-preview-label">Daily:</span>
              <span className={`usage-preview-value ${dailyAlertLevel}`}>
                {usage.daily_usage}/{usage.daily_limit}
              </span>
              <div className="usage-preview-bar">
                <div 
                  className={`usage-preview-fill ${dailyAlertLevel}`}
                  style={{ width: `${Math.min(100, (usage.daily_usage / usage.daily_limit) * 100)}%` }}
                ></div>
              </div>
            </div>
            
            <div className="usage-preview-item">
              <span className="usage-preview-label">Monthly:</span>
              <span className={`usage-preview-value ${monthlyAlertLevel}`}>
                {usage.monthly_usage}/{usage.monthly_limit}
              </span>
              <div className="usage-preview-bar">
                <div 
                  className={`usage-preview-fill ${monthlyAlertLevel}`}
                  style={{ width: `${Math.min(100, (usage.monthly_usage / usage.monthly_limit) * 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}
        
        {/* Usage alerts */}
        {usage && (dailyAlertLevel === 'critical' || monthlyAlertLevel === 'critical') && (
          <div className="usage-alert critical">
            ⚠️ This API key is approaching its usage limits. Consider upgrading your plan.
          </div>
        )}
        
        {usage && (dailyAlertLevel === 'warning' || monthlyAlertLevel === 'warning') && (
          <div className="usage-alert">
            ⚠️ High usage detected. Monitor your usage to avoid hitting limits.
          </div>
        )}
      </div>
      
      <div className="api-key-actions">
        <button 
          className="usage-button"
          onClick={() => onViewUsage(keyData.key)}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'View Details'}
        </button>
        <button 
          className="revoke-button"
          onClick={() => onRevoke(keyData.id)}
        >
          Revoke
        </button>
      </div>
    </div>
  );
};

export default Dashboard;